import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { Video } from 'js/src/client';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { RootState } from 'js/src/libs/RootState';

type MultiVideosState = {
    videos: PlainJsStoreOrmData<Video, number>;
};

const initialState: MultiVideosState = {
    videos: PlainJsStoreORM.createEmpty<Video, number>().data,
};

export const multiVideosSlice = createSlice({
    name: 'multiVideos',
    initialState,
    reducers: {
        setMultiVideos: (state, action: PayloadAction<{ videos: Video[] }>) => {
            const orm = new PlainJsStoreORM(state.videos);
            action.payload.videos.forEach((video) => {
                orm.pushItem(video, 'id');
            });
            state.videos = orm.data;
        },
        setVideoName: (
            state,
            action: PayloadAction<{ videoName: string; videoId: number | null }>,
        ) => {
            const orm = new PlainJsStoreORM(state.videos);
            if (action.payload.videoId === null || !orm.getById(action.payload.videoId)) {
                return;
            }
            orm.updateById(action.payload.videoId, (video) => ({
                ...video,
                name: action.payload.videoName,
            }));
            state.videos = orm.data;
        },
    },
    selectors: {
        getMultiVideos: (state) => state.videos,
        hasMultiVideos: (state) => {
            const orm = new PlainJsStoreORM(state.videos);
            return orm.length > 1;
        },
        getMultiVideoCount: (state) => {
            const orm = new PlainJsStoreORM(state.videos);
            return orm.length;
        },
        getMultiVideoByVideoId: (state, videoId: number) => {
            const orm = new PlainJsStoreORM(state.videos);
            return orm.getById(videoId);
        },
        getMultiVideoIds: (state) => {
            const orm = new PlainJsStoreORM(state.videos);
            return orm.allIds;
        },
    },
});

export const { setMultiVideos, setVideoName } = multiVideosSlice.actions;
export const {
    getMultiVideos,
    hasMultiVideos,
    getMultiVideoCount,
    getMultiVideoByVideoId,
    getMultiVideoIds,
} = multiVideosSlice.getSelectors((state: RootState) => state.app.multiVideos);
export default multiVideosSlice.reducer;
